import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MessageSquare, Trash2, Mic, FileText, Loader2, Pin, PinOff, Search,
} from "lucide-react";

const PIN_KEY = "nexa_pinned_convos_v1";

function loadPins() {
  try { return new Set(JSON.parse(localStorage.getItem(PIN_KEY) || "[]")); }
  catch { return new Set(); }
}
function savePins(s) {
  try { localStorage.setItem(PIN_KEY, JSON.stringify([...s])); } catch {}
}

// Bucket keys (i18n) — resolved in render to keep this function pure.
function timeBucket(iso) {
  if (!iso) return "older";
  const now = new Date();
  const d = new Date(iso);
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (d.toDateString() === now.toDateString()) return "today";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "yesterday";
  if (diffDays < 7) return "last_7";
  if (diffDays < 30) return "last_30";
  return "older";
}

const BUCKET_ORDER = ["today", "yesterday", "last_7", "last_30", "older"];

export default function Sidebar({ conversations, activeId, onDelete, recordings = [], onOpenTranscript }) {
  const { t } = useTranslation();
  const [pins, setPins] = useState(loadPins);
  const [query, setQuery] = useState("");

  const recByConv = useMemo(() => {
    const m = new Map();
    for (const r of recordings) m.set(r.conversation_id, r);
    return m;
  }, [recordings]);

  const togglePin = (id, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setPins((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      savePins(next);
      return next;
    });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) =>
      (c.title || "").toLowerCase().includes(q),
    );
  }, [conversations, query]);

  const pinned = filtered.filter((c) => pins.has(c.id));
  const unpinned = filtered.filter((c) => !pins.has(c.id));

  // Group unpinned by recency
  const groups = useMemo(() => {
    const g = {};
    for (const c of unpinned) {
      const b = timeBucket(c.updated_at || c.created_at);
      (g[b] = g[b] || []).push(c);
    }
    return g;
  }, [unpinned]);

  const bucketLabel = (key) => t(`workspace:sidebar.group.${key}`);
  const untitled = t("workspace:palette.untitled");

  return (
    <div className="flex-1 overflow-y-auto" data-testid="sidebar">
      {/* Search */}
      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("workspace:sidebar.search_placeholder")}
            className="w-full pl-7 pr-2 py-1.5 text-sm rounded-sm border border-border bg-background placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            data-testid="sidebar-search-input"
          />
        </div>
      </div>

      {/* Empty */}
      {conversations.length === 0 && (
        <div className="px-3 py-4 text-sm text-muted-foreground">{t("workspace:sidebar.no_chats_yet")}</div>
      )}

      {conversations.length > 0 && filtered.length === 0 && (
        <div className="px-3 py-4 text-xs text-muted-foreground font-mono">{t("workspace:sidebar.no_matches", { q: query })}</div>
      )}

      {/* Pinned section */}
      {pinned.length > 0 && (
        <Group label={t("workspace:sidebar.pinned")} icon={Pin}>
          {pinned.map((c) => (
            <ConvRow key={c.id} c={c} activeId={activeId} pinned onTogglePin={togglePin}
              onDelete={onDelete} rec={recByConv.get(c.id)} onOpenTranscript={onOpenTranscript} t={t} untitled={untitled} />
          ))}
        </Group>
      )}

      {/* Recency groups */}
      {BUCKET_ORDER.map((bucket) => {
        const items = groups[bucket];
        if (!items?.length) return null;
        return (
          <Group key={bucket} label={bucketLabel(bucket)}>
            {items.map((c) => (
              <ConvRow key={c.id} c={c} activeId={activeId} pinned={false} onTogglePin={togglePin}
                onDelete={onDelete} rec={recByConv.get(c.id)} onOpenTranscript={onOpenTranscript} t={t} untitled={untitled} />
            ))}
          </Group>
        );
      })}
    </div>
  );
}

function Group({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground flex items-center gap-1.5">
        {Icon && <Icon className="h-2.5 w-2.5" />}
        {label}
      </div>
      <ul className="space-y-px">{children}</ul>
    </div>
  );
}

function ConvRow({ c, activeId, pinned, onTogglePin, onDelete, rec, onOpenTranscript, t, untitled }) {
  const isActive = c.id === activeId;
  return (
    <li>
      <div
        className={`group flex items-center gap-2 px-3 py-2 hover:bg-background transition-colors ${
          isActive ? "bg-background border-l-2 border-primary" : ""
        }`}
      >
        <Link
          to={`/workspace/${c.id}`}
          className="flex-1 min-w-0 flex items-center gap-2"
          data-testid={`conv-item-${c.id}`}
        >
          {c.is_voice ? (
            <Mic className="h-3.5 w-3.5 text-primary shrink-0" />
          ) : (
            <MessageSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          <span className="text-sm truncate">{c.title || untitled}</span>
        </Link>
        {rec && (
          <button
            onClick={() => onOpenTranscript?.(rec.id)}
            className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border rounded-sm transition ${
              rec.status === "ready"
                ? "border-primary/40 text-primary hover:bg-primary/10"
                : rec.status === "failed"
                ? "border-destructive/40 text-destructive hover:bg-destructive/10"
                : "border-amber-500/40 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
            }`}
            data-testid={`conv-transcript-${c.id}`}
          >
            {rec.status === "ready" ? (
              <span className="flex items-center gap-1"><FileText className="h-2.5 w-2.5" />{t("workspace:transcript")}</span>
            ) : rec.status === "failed" ? t("workspace:transcript_failed") : (
              <span className="flex items-center gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" />{t("workspace:transcribing")}</span>
            )}
          </button>
        )}
        <button
          onClick={(e) => onTogglePin(c.id, e)}
          className={`transition ${pinned ? "text-primary" : "opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground"}`}
          data-testid={`conv-pin-${c.id}`}
          aria-label={pinned ? t("workspace:unpin", "Desfijar") : t("workspace:pin", "Fijar")}
          title={pinned ? t("workspace:unpin", "Desfijar") : t("workspace:pin", "Fijar")}
        >
          {pinned ? <Pin className="h-3 w-3 fill-current" /> : <PinOff className="h-3 w-3" />}
        </button>
        <button
          onClick={() => onDelete(c.id)}
          className="opacity-0 group-hover:opacity-100 transition text-muted-foreground hover:text-destructive"
          data-testid={`conv-delete-${c.id}`}
          aria-label={t("workspace:delete_conv", "Borrar conversación")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}
